import {
  type ApolloClient,
  type DocumentNode,
  type ErrorLike,
  type ErrorPolicy,
  type OperationVariables,
  type TypedDocumentNode,
} from "@apollo/client";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import {
  useLazyQuery as apolloUseLazyQuery,
  useMutation as apolloUseMutation,
  useQuery as apolloUseQuery,
  skipToken,
  type MutationHookOptions,
  type SkipToken,
  type SuspenseQueryHookOptions,
  type UseSuspenseQueryResult,
} from "@apollo/client/react";
import { useAutoSaveStatus, useSnackbar, type UseSnackbarHook } from "@homebound/beam";
import { useCallback, useEffect, useRef } from "react";

export { skipToken };

/**
 * Compatibility boundary for `typescript-react-apollo`, which still generates Apollo 3-shaped hooks.
 *
 * Both Codegen import settings in `graphql-codegen.ts` point generated hooks here. We intentionally
 * preserve only the contracts the generated signatures and existing callers rely on:
 *
 * - skipped queries may omit required variables;
 * - lazy queries may receive variables at hook creation and merge them with execution variables;
 * - query results expose complete operation data rather than Apollo 4's `DeepPartial` data-state union;
 * - mutation execution remains optional when variables were configured at hook creation, while its
 *   resolved result still reflects Apollo 4's `errorPolicy` semantics.
 *
 * This does not recreate Apollo 3 wholesale. Removed query callbacks stay removed, errors use Apollo 4's
 * `ErrorLike`, and all runtime work is delegated to Apollo 4 hooks.
 */
/**
 * Native Apollo 4 names required by Codegen's hook templates. The generated suspense hooks compile against
 * these exports but remain intentionally unsupported by the throwing `useSuspenseQuery` wrapper below.
 */
export type { MutationHookOptions, SkipToken, SuspenseQueryHookOptions, UseSuspenseQueryResult };

/** Allows Codegen's `{ skip: true }` overload to omit otherwise-required query variables. */
export type QueryHookOptions<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = apolloUseQuery.Base.Options<TData, TVariables> & { variables?: TVariables };

/** Retains Apollo 3's hook-level lazy variables, which our wrapper merges into each execution. */
export type LazyQueryHookOptions<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = apolloUseLazyQuery.Options<TData, TVariables> & { variables?: TVariables };

/** Native Apollo 4 hook state, exported under the name emitted by Codegen's result aliases. */
export type MutationResult<TData = unknown> = apolloUseMutation.Result<TData>;

/**
 * Apollo 3 treated query data as complete or absent. We retain that contract because existing tables,
 * forms, PDF renderers, and generated `*QueryResult` aliases are not designed for Apollo 4's
 * `DeepPartial<TData>` states. Callers requesting partial or streaming data must use Apollo directly.
 */
export type QueryResult<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = Apollo3QueryResult<apolloUseQuery.Result<TData, TVariables>, TData>;

/** Lazy-query equivalent of `QueryResult`'s complete-data compatibility contract. */
export type LazyQueryResult<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = Apollo3QueryResult<apolloUseLazyQuery.Result<TData, TVariables>, TData>;

type LazyQueryResultTuple<TData = unknown, TVariables extends OperationVariables = OperationVariables> = [
  (options?: apolloUseLazyQuery.ExecOptions<TVariables>) => Promise<ApolloClient.QueryResult<TData>>,
  LazyQueryResult<TData, TVariables>,
];

/** Internal tuple used to return the Apollo 3-shaped execute function with Apollo 4 hook state. */
type MutationResultTuple<TData, TVariables extends OperationVariables> = [
  MutationFunction<TData, TVariables>,
  MutationResult<TData>,
];

/** Wraps the Apollo `useQuery` with default `onError` behavior. */
export function useQuery<TData = unknown, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryHookOptions<TData, TVariables>,
): QueryResult<TData, TVariables>;
export function useQuery(query: DocumentNode, options?: QueryHookOptions): QueryResult {
  const { triggerNotice, closeNotice } = useSnackbar();
  const result = apolloUseQuery(query, options);
  useSnackbarOnQueryError(result.error, triggerNotice, closeNotice);
  return result as QueryResult;
}

/** Wraps the Apollo `useQuery` with default `onError` behavior. */
export function useLazyQuery<TData = unknown, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: LazyQueryHookOptions<TData, TVariables>,
): LazyQueryResultTuple<TData, TVariables>;
export function useLazyQuery(query: DocumentNode, options?: LazyQueryHookOptions): LazyQueryResultTuple {
  const { triggerNotice, closeNotice } = useSnackbar();
  const [originalExecute, result] = apolloUseLazyQuery(query, options);
  const execute = useCallback(
    (executeOptions?: apolloUseLazyQuery.ExecOptions<OperationVariables>) =>
      originalExecute({ ...executeOptions, variables: { ...options?.variables, ...executeOptions?.variables } }),
    [options?.variables, originalExecute],
  );
  useSnackbarOnQueryError(result.error, triggerNotice, closeNotice);
  return [execute, result] as unknown as LazyQueryResultTuple;
}

export function useSuspenseQuery<TData = unknown, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: SkipToken | SuspenseQueryHookOptions<TData, TVariables>,
) {
  throw new Error(
    "We don't currently support using suspense queries. If we do want to support this in the future, we need to use the error boundary pattern with our custom error handling logic.",
  );
}

/**
 * Wraps the `Apollo.useMutation` hook with `useAutoSaveStatus` integration.
 *
 * This means that any mutation in the app will automatically register its
 * in-flight status with the right contextual auto-save indicator (e.g. if
 * a mutation is in a super drawer, and the super drawer has a custom/local
 * save indicator, then the mutation will trigger that indicator and not
 * a global/page-level indicator.
 */
export function useMutation<TData = unknown, TVariables extends OperationVariables = OperationVariables>(
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: MutationHookOptions<TData, TVariables>,
): MutationResultTuple<TData, TVariables> {
  const { triggerNotice, closeNotice } = useSnackbar();
  const { onError: baseOnError, onCompleted: baseOnCompleted, ...restOptions } = options || {};

  const failed = useRef(false);

  const onError = useCallback(
    (
      onExecutionOnError?: MutationHookOptions<TData, TVariables>["onError"],
    ): MutationHookOptions<TData, TVariables>["onError"] =>
      (error) => {
        failed.current = true;
        if (baseOnError || onExecutionOnError) {
          baseOnError?.(error);
          onExecutionOnError?.(error);
        } else {
          snackbarOnError("mutation", triggerNotice, closeNotice, error);
        }
      },
    [baseOnError, triggerNotice, closeNotice],
  );

  const onCompleted = useCallback(
    (
      onExecutionOnCompleted?: MutationHookOptions<TData, TVariables>["onCompleted"],
    ): MutationHookOptions<TData, TVariables>["onCompleted"] =>
      (data, clientOptions) => {
        baseOnCompleted?.(data, clientOptions);
        onExecutionOnCompleted?.(data, clientOptions);
        if (failed.current) {
          closeNotice(apolloErrorNoticeId);
          failed.current = false;
        }
      },
    [baseOnCompleted, closeNotice],
  );

  const { triggerAutoSave, resolveAutoSave } = useAutoSaveStatus();
  const triggered = useRef(false);

  const [originalExecute, result] = apolloUseMutation(
    mutation as DocumentNode,
    { ...restOptions, onError: onError(), onCompleted: onCompleted() } as MutationHookOptions,
  ) as unknown as MutationResultTuple<TData, TVariables>;
  const isLoading = result.loading;

  useEffect(() => {
    // Only trigger/resolve if we've changed our status
    if (isLoading !== triggered.current) {
      isLoading ? triggerAutoSave() : resolveAutoSave();
      triggered.current = isLoading;
    }
    return () => {
      // On unmount, if we have an outstanding auto save, resolve it
      triggered.current && resolveAutoSave();
    };
  }, [isLoading, triggerAutoSave, resolveAutoSave]);

  /**
   * Override the execute function to wrap the onCompleted and onError behavior.
   *
   * This allow us to close the snackbar onCompleted and show the snackbar onError
   * even if and onCompleted has been provided at the time of the hook call.
   */
  const execute = useCallback(
    async (options?: MutationExecutionOptions<TData, TVariables>) => {
      return await originalExecute({
        ...options,
        onCompleted: onCompleted(options?.onCompleted),
        onError: onError(options?.onError),
      });
    },
    [onCompleted, onError, originalExecute],
  );

  return [execute, result] as unknown as MutationResultTuple<TData, TVariables>;
}

type Apollo3QueryResult<TResult, TData> = Omit<TResult, "data" | "previousData"> & {
  data: TData | undefined;
  previousData?: TData;
};

/**
 * Apollo 4 requires mutation options when required variables were not configured on the hook. Codegen's
 * Apollo 3-shaped hooks do not carry that configured-variable type information, so their execute function
 * historically accepted no arguments. Keeping the options optional preserves calls whose variables were
 * supplied at hook creation; Apollo still reports missing required variables at runtime.
 *
 * The execution-level policy remains generic so Apollo 4's native result type can enforce that `none`
 * rejects, `ignore` strips errors, and only `all` resolves with an `error` property.
 */
type MutationFunction<TData, TVariables extends OperationVariables> = <TErrorPolicy extends ErrorPolicy = "none">(
  options?: MutationFunctionOptionsForPolicy<TData, TVariables, TErrorPolicy>,
) => Promise<ApolloClient.MutateResult<TData, TErrorPolicy>>;

type MutationExecutionOptions<TData = unknown, TVariables extends OperationVariables = OperationVariables> = Omit<
  apolloUseMutation.MutationFunctionOptions<TData, TVariables>,
  "variables"
> & { variables?: TVariables };

type MutationFunctionOptionsForPolicy<
  TData,
  TVariables extends OperationVariables,
  TErrorPolicy extends ErrorPolicy,
> = Omit<MutationExecutionOptions<TData, TVariables>, "errorPolicy"> & { errorPolicy?: TErrorPolicy };

/** Shows an error snackbar whenever a wrapped query surfaces an error. */
function useSnackbarOnQueryError(
  error: ErrorLike | undefined,
  triggerNotice: UseSnackbarHook["triggerNotice"],
  closeNotice: UseSnackbarHook["closeNotice"],
): void {
  useEffect(() => {
    if (error) snackbarOnError("query", triggerNotice, closeNotice, error);
  }, [error, triggerNotice, closeNotice]);
}

function snackbarOnError(
  type: "query" | "mutation",
  triggerNotice: UseSnackbarHook["triggerNotice"],
  closeNotice: UseSnackbarHook["closeNotice"],
  error: ErrorLike,
): void {
  const errors = CombinedGraphQLErrors.is(error) ? error.errors : undefined;

  // If the call failed for some not-our-backend's fault, i.e. maybe the cognito token
  // not refresh, still tell the user that they're operation/save didn't work.
  let message = !errors || errors.length === 0 ? error.message : errors.map((e) => e.message).join(", ");

  // If we had a network error on a mutation set a custom message
  if (message.startsWith("Fetch failed calling") && type === "mutation") {
    message =
      "The operation timed out, but may actually succeed, so please reload the page or wait before trying again.";
  }

  const isGraphqlValidationError = message === "Graphql validation error";

  // Stories often omit GraphQL mocks; skip the snackbar UI so Chromatic does not capture it.
  if (isStorybook()) {
    console.error(message);
    if (type === "mutation") throw error;
    return;
  }

  // One Apollo error notice at a time: close-then-show so a page of failed queries
  // does not stack the same (or a newer) message.
  closeNotice(apolloErrorNoticeId);
  triggerNotice({
    id: apolloErrorNoticeId,
    icon: "error",
    persistent: true,
    message: isGraphqlValidationError ? graphqlValidationErrorMessage : message,
    action: isGraphqlValidationError
      ? { label: "Reload", onClick: () => window.location.reload(), variant: "text" }
      : undefined,
  });

  // Trigger a console.error for DataDog RUM to report on.
  console.error(message);

  // If a component is invoking a mutation in an event callback like:
  //
  // const [saveAuthor] = useSaveAuthor();
  //
  // function onClick() {
  //   const { data } = await saveMutation(...)
  //   console.log(data.foo.bar);
  // }
  //
  // The default Apollo behavior is for that promise to be rejected, and so
  // the console.log will never happen. For our snackbarOnError instrumentation to
  // maintain the same behavior, we need to re-throw the error from our onError.
  if (type === "mutation") {
    throw error;
  }
}

const apolloErrorNoticeId = "apollo-error";
const graphqlValidationErrorMessage =
  "This version of Blueprint is out of date and your request could not be processed. You need to reload the page to continue.";

function isStorybook(): boolean {
  return Boolean((import.meta as { env?: { STORYBOOK?: boolean } }).env?.STORYBOOK);
}
